import PropTypes from 'prop-types';
import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import VerificationInput from 'react-verification-input';
import Button from './components/Button';
import utils from 'util/utils';
import { connect } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'assets/images';
import {
  sendRemoteCode,
  validateRemoteCode
} from 'store/modules/login/actions';
import withBoundary from 'component/ErrorBoundary/withBoundary';

const EmailAuthentication = ({ sendCode, verifyCode }) => {
  const { state } = useLocation();
  const [email, setEmail] = React.useState(state?.email);
  const [error, setError] = React.useState(null);
  const [open, setOpen] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState('');
  const classes = useStyles();
  const navigate = useNavigate();
  const handleChange = (event) => {
    setEmail(event.target.value);
  };
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(null);
  };
  const verifyRemoteCode = () => {
    setLoading(true);
    utils.clearUserEmail();
    verifyCode(email, value, (status, response) => {
      setLoading(false);
      if (status && response) {
        setOpen('Login successful');
        setTimeout(() => navigate('/settings'), 2000);
      } else {
        setError(response?.message || 'error');
      }
    });
  };
  const resendCode = () => {
    if (!loading) {
      setLoading(true);
      sendCode(email, (status) => {
        setLoading(false);
        if (status) {
          setOpen('code resent');
        }
      });
    }
  };

  return (
    <Box className="outerContainer">
      <Snackbar
        autoHideDuration={6000}
        onClose={handleClose}
        open={open !== null}>
        <Alert onClose={handleClose} severity="success" sx={{ width: '100%' }}>
          {open}
        </Alert>
      </Snackbar>
      <Link className={classes.link} to={-1}>
        <ArrowLeft height={20} style={{ marginRight: '9px' }} width={20} />
        <Box className={classes.subHeader}>back</Box>
      </Link>
      <Box className="center" sx={{ padding: '24px 16px', height: '100%' }}>
        <Box className={classes.subHeader} style={{ marginBottom: '16px' }}>
          Please enter the registered email address of your account
        </Box>
        <Box className="flex" sx={{ marginBottom: '20px', width: '100%' }}>
          <TextField
            className="textEmail"
            id="standard-required"
            label="Email"
            onChange={handleChange}
            value={email}
            variant="standard"
          />
          <Box sx={{ marginLeft: '16px' }}>
            <Button gray={true} variant="contained">
              Submit
            </Button>
          </Box>
        </Box>
        <Box className={classes.hint} style={{ marginBottom: '16px' }}>
          Please check your registered email for the 6-digit passcode
        </Box>
        <Box className={classes.hint}>{email}</Box>
        <div className="codeStyle">
          <VerificationInput
            autoFocus={true}
            classNames={{
              container: 'vinput-container',
              character: 'vinput-character',
              characterInactive: 'vinput-character-inactive',
              characterSelected: 'vinput-character--selected'
            }}
            length={6}
            onChange={setValue}
            placeholder="0"
            value={value}
          />
        </div>

        {error ? (
          <React.Fragment>
            <Box className={classes.error} style={{ marginTop: '10px' }}>
              Sorry, the 6-digit passcode you entered isn’t valid,
              <br /> please contact your Video Window administrator for
              assistance
            </Box>
            <Box className={classes.hint} style={{ marginTop: '16px' }}>
              or
            </Box>
            <Link
              className="link"
              to={-1}
              onClick={() => utils.clearUserEmail()}>
              <Button blue={true} variant="contained">
                Try again
              </Button>
            </Link>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Box
              className="flex"
              sx={{
                margin: '20px 0'
              }}>
              <Button
                blueOutlined={true}
                variant="outlined"
                to={-1}
                onClick={() => utils.clearUserEmail()}>
                Cancel
              </Button>
              <Button
                blue={true}
                loading={loading}
                onClick={verifyRemoteCode}
                variant="contained">
                Submit
              </Button>
            </Box>
            <Box className={classes.emailView}>
              <Box className={classes.footer}>
                It may take a minute to receive your code
              </Box>
              <Box className={classes.footer}>
                Haven’t received it?
                <Box
                  className="link"
                  onClick={resendCode}
                  style={{ color: '#10AFE2', marginLeft: '2px' }}>
                  Resend
                </Box>
                a new code
              </Box>
              <Box className={classes.footer}>
                Also, check your spam folder, just in case
              </Box>
            </Box>
          </React.Fragment>
        )}
      </Box>
    </Box>
  );
};

EmailAuthentication.propTypes = {
  sendCode: PropTypes.func,
  verifyCode: PropTypes.func
};
const mapStateToProps = () => {
  return {
    //
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    verifyCode: (email, code, callback) =>
      dispatch(validateRemoteCode(email, code, callback)),
    sendCode: (email, callback) => dispatch(sendRemoteCode(email, callback))
  };
};

const useStyles = makeStyles((theme) => ({
  link: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '50px',
    marginBottom: '16px',
    textDecoration: 'none'
  },
  emailView: {
    display: 'flex',
    justifyContent: 'space-between',
    '@media (max-width: 600px)': {
      display: 'block'
    }
  },
  header: {
    ...theme.typography.labelRegular,
    color: theme.palette.text07Info
  },
  subHeader: {
    ...theme.typography.labelSmall,
    color: theme.palette.text07Info
  },
  hint: {
    ...theme.typography.labelTiny,
    color: theme.palette.text07Info
  },
  error: {
    ...theme.typography.labelTiny,
    color: theme.palette.actionDanger,
    error: '20px'
  },
  footer: {
    ...theme.typography.labelTiny,
    color: theme.palette.text07Info,
    flex: 1,
    textAlign: 'center',
    '@media (minWidth: 600px)': {
      width: '30%'
    }
  },
  footerLink: {
    ...theme.typography.labelTiny,
    color: theme.palette.screen01
  }
}));

export default React.memo(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withBoundary(EmailAuthentication))
);
